/**
 * CLE-2811 — a small, safe Markdown renderer for the AI's messages in the conversation.
 *
 * The SPA has no Markdown library and this does not add one. It covers what the agent actually writes:
 * paragraphs (split on blank lines, single line breaks kept), `**bold**`, `*italic*`, `` `code` ``,
 * fenced code blocks, `- ` / `* ` bullet lists, `1. ` numbered lists and `#`–`######` headings (shown as a
 * bold line) — plus rate lists: three or more consecutive "<code> <amount>" rows become a monospace block
 * (`splitScheduleBlocks`). Everything else is literal text.
 *
 * Safety: the output is React elements built from strings, never `dangerouslySetInnerHTML`, so an HTML
 * tag in a reply is shown as the characters it is. Nesting is deliberately shallow — no nested lists,
 * no formatting inside bold or italic — and an unclosed fence is just text (a streamed reply is often
 * mid-fence).
 */

import React from "react";
import styled from "styled-components";

import { splitScheduleBlocks } from "./conversation";

// ---------------------------------------------------------------------------------------------
// The model: blocks and inline runs. Pure, so the parsing is tested without a DOM.
// ---------------------------------------------------------------------------------------------

export type IChatInline =
  | { kind: "text"; text: string }
  | { kind: "bold"; text: string }
  | { kind: "italic"; text: string }
  | { kind: "code"; text: string };

export type IChatBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "heading"; text: string }
  | { kind: "bullets"; items: string[] }
  | { kind: "numbered"; start: number; items: string[] }
  | { kind: "code"; text: string }
  | { kind: "schedule"; text: string };

// Code first, so `**` inside a code span stays literal; bold before italic, so `**` is not two italics.
// Italic needs a non-space right after the opening `*` and right before the closing one ("a * b * c" is
// not italic).
const INLINE =
  /(`[^`\n]+`)|(\*\*(?=\S)[^*\n]*?\S\*\*)|(\*(?=[^\s*])[^*\n]*?[^\s*]\*|\*[^\s*]\*)/g;

/** The inline runs of one line or paragraph. */
export const parseInline = (text: string): IChatInline[] => {
  const out: IChatInline[] = [];
  let last = 0;
  text.replace(INLINE, (match, code, bold, italic, offset: number) => {
    if (offset > last)
      out.push({ kind: "text", text: text.slice(last, offset) });
    if (code) out.push({ kind: "code", text: code.slice(1, -1) });
    else if (bold) out.push({ kind: "bold", text: bold.slice(2, -2) });
    else if (italic) out.push({ kind: "italic", text: italic.slice(1, -1) });
    last = offset + match.length;
    return match;
  });
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
};

const FENCE = /^\s*```/;
const FENCE_CLOSE = /^\s*```\s*$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,4})[.)]\s+(.*)$/;

/** Headings, lists and paragraphs of text that has no fences and no schedule rows. */
const parseLines = (text: string, out: IChatBlock[]) => {
  const lines = text.split("\n");
  let para: string[] = [];
  const flush = () => {
    if (para.length) out.push({ kind: "paragraph", text: para.join("\n") });
    para = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const heading = HEADING.exec(line);
    const numbered = NUMBERED.exec(line);
    if (!line.trim()) {
      flush();
      i += 1;
    } else if (heading) {
      flush();
      out.push({ kind: "heading", text: heading[1].trim() });
      i += 1;
    } else if (BULLET.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && BULLET.test(lines[i])) {
        items.push((BULLET.exec(lines[i]) as RegExpExecArray)[1]);
        i += 1;
      }
      out.push({ kind: "bullets", items });
    } else if (numbered) {
      flush();
      const items: string[] = [];
      while (i < lines.length && NUMBERED.test(lines[i])) {
        items.push((NUMBERED.exec(lines[i]) as RegExpExecArray)[2]);
        i += 1;
      }
      out.push({ kind: "numbered", start: Number(numbered[1]), items });
    } else {
      para.push(line);
      i += 1;
    }
  }
  flush();
};

/** Text between fences: schedule blocks out first, then headings, lists and paragraphs. */
const parseProse = (text: string, out: IChatBlock[]) => {
  splitScheduleBlocks(text).forEach((seg) => {
    if (seg.kind === "schedule") out.push({ kind: "schedule", text: seg.text });
    else parseLines(seg.text, out);
  });
};

/** The blocks of a message body. */
export const parseChatMarkdown = (source: string): IChatBlock[] => {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const out: IChatBlock[] = [];
  let prose: string[] = [];
  const flushProse = () => {
    if (prose.length) parseProse(prose.join("\n"), out);
    prose = [];
  };
  let i = 0;
  while (i < lines.length) {
    if (FENCE.test(lines[i])) {
      let close = i + 1;
      while (close < lines.length && !FENCE_CLOSE.test(lines[close]))
        close += 1;
      if (close < lines.length) {
        flushProse();
        out.push({ kind: "code", text: lines.slice(i + 1, close).join("\n") });
        i = close + 1;
      } else {
        // Unclosed: the fence line is just text, and so is everything after it.
        prose.push(lines[i]);
        i += 1;
      }
    } else {
      prose.push(lines[i]);
      i += 1;
    }
  }
  flushProse();
  return out;
};

// ---------------------------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------------------------

const Body = styled.div`
  font-size: 0.9rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
  word-break: break-word;

  & > * + * {
    margin-top: 0.6rem;
  }

  p {
    margin: 0;
    white-space: pre-wrap;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.3rem;
  }

  li + li {
    margin-top: 0.2rem;
  }

  code {
    padding: 0.05rem 0.3rem;
    border-radius: 4px;
    background: var(--surface-100, #f5f5f5);
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85em;
  }
`;

/** A code block or a schedule: monospace on a light ground, wrapping — never a scroller of its own. */
const Pre = styled.pre`
  margin: 0;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  background: var(--surface-100, #f5f5f5);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  code {
    padding: 0;
    background: none;
    font-size: inherit;
  }
`;

const Heading = styled.p`
  font-weight: 600;
`;

const renderInline = (text: string): React.ReactNode[] =>
  parseInline(text).map((run, i) => {
    const key = `${i}:${run.kind}`;
    switch (run.kind) {
      case "bold":
        return <strong key={key}>{run.text}</strong>;
      case "italic":
        return <em key={key}>{run.text}</em>;
      case "code":
        return <code key={key}>{run.text}</code>;
      default:
        return <React.Fragment key={key}>{run.text}</React.Fragment>;
    }
  });

const renderBlock = (
  block: IChatBlock,
  key: string,
  trailing?: React.ReactNode
): React.ReactNode => {
  switch (block.kind) {
    case "heading":
      return (
        <Heading key={key}>
          {renderInline(block.text)}
          {trailing}
        </Heading>
      );
    case "bullets":
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case "numbered":
      return (
        <ol key={key} start={block.start}>
          {block.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ol>
      );
    case "code":
      return (
        <Pre key={key}>
          <code>{block.text}</code>
        </Pre>
      );
    case "schedule":
      return (
        <Pre key={key} data-kind="schedule">
          {block.text}
        </Pre>
      );
    default:
      return (
        <p key={key}>
          {renderInline(block.text)}
          {trailing}
        </p>
      );
  }
};

interface IChatMarkdownProps {
  text: string;
  /** Rendered after the last block's content — the live reply's typing cursor. */
  trailing?: React.ReactNode;
}

/** A message body as formatted React elements. */
function ChatMarkdown({ text, trailing }: IChatMarkdownProps) {
  const blocks = parseChatMarkdown(text);
  const last = blocks[blocks.length - 1];
  // The cursor sits at the end of the last line when that is prose; after a list or a block, below it.
  const inline =
    !!trailing &&
    !!last &&
    (last.kind === "paragraph" || last.kind === "heading");
  return (
    <Body>
      {blocks.map((b, i) =>
        renderBlock(
          b,
          `${i}:${b.kind}`,
          inline && i === blocks.length - 1 ? trailing : undefined
        )
      )}
      {trailing && !inline ? <div>{trailing}</div> : null}
    </Body>
  );
}

export default ChatMarkdown;
